import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import { FormMessage } from 'components/forms/FormProps';
import { AdminChangePasswordFormData } from 'components/forms/user/AdminChangePasswordForm';
import UserForm, { UserFormData } from 'components/forms/user/UserForm';
import { USER_REDIRECT_AFTER_SAVE } from 'env';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useState } from 'react';
import {
  mapUserFormToUpdatePayload,
  mapUserResponseToFormInitialValues,
} from 'pages/admin-module/user/user-page-mappers';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { USERS_PAGE_PATH } from 'routes/paths';
import {
  DUPLICATE_USER_ERROR_CODE,
  ERROR_NOTIFICATION_COLOR,
  INSUFFICIENT_RIGHTS_ERROR_CODE,
  PASSWORD_REUSE_ERROR_CODE,
  SUCCESS_NOTIFICATION_COLOR,
} from 'utils/constants';

/**
 * Page used to edit a user.
 *
 */
export default function EditUserPage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { getAction, roleId: currentUserRoleId } = useApi();
  const [passwordChangeMessage, setPasswordChangeMessage] = useState<FormMessage | undefined>(undefined);
  const { getPermissionTemplate, getDefaultPermissionTemplate } = usePermissionDataProvider();

  const { data, loading, error } = useImmediateAction(() => {
    const userGetAction = getAction('UserGet');
    return userGetAction({ parameters: { userId: userId! } });
  });

  if (error) {
    panic(error);
    return <></>;
  }

  if (!userId) {
    return <Navigate to={USERS_PAGE_PATH.original} />;
  }

  /**
   * Handles the form submission.
   */
  function onSubmit({
    preNominals,
    postNominals,
    firstName,
    lastName,
    email,
    nfqesEmail,
    phoneNumber,
    roleId,
    globalPermissions,
    inOrganizations,
    inDepartments,
  }: UserFormData) {
    const userUpdateAction = getAction('UserUpdate');

    return userUpdateAction({
      parameters: { userId: userId! },
      payload: mapUserFormToUpdatePayload(
        {
          preNominals,
          postNominals,
          firstName,
          lastName,
          email,
          nfqesEmail,
          phoneNumber,
          roleId,
          globalPermissions,
          inOrganizations,
          inDepartments,
          organizationId: '',
          password: '',
          confirmPassword: '',
          copyProfilesFromUserId: undefined,
        },
        getPermissionTemplate,
        getDefaultPermissionTemplate,
        {
          includeGlobalPermissions: currentUserRoleId === ROLE_ADMIN_ID,
        }
      ),
    })
      .then(() => {
        notifications.show({
          title: 'Používateľ upravený',
          message: `Používateľ ${firstName} ${lastName} bol úspešne aktualizovaný.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        if (USER_REDIRECT_AFTER_SAVE === 'list') {
          navigate(USERS_PAGE_PATH.original);
        }
      })
      .catch((error) => {
        if (error.response.data.error.code === DUPLICATE_USER_ERROR_CODE) {
          notifications.show({
            title: 'Chyba!',
            message: 'Používateľ s danou emailovou adresou už existuje.',
            color: ERROR_NOTIFICATION_COLOR,
          });
        } else {
          panic(error);
        }
      });
  }

  /**
   * Handles the password change.
   */
  function changePassword({ newPassword, newPasswordRepeat }: AdminChangePasswordFormData) {
    const adminChangePasswordAction = getAction('UserAdminChangePassword');

    return adminChangePasswordAction({
      payload: {
        newPassword,
        newPasswordRepeat,
      },
      parameters: {
        userId: userId!,
      },
    })
      .then(() => {
        setPasswordChangeMessage({ type: 'success', message: 'Heslo bolo úspešne zmenené.' });
      })
      .catch((e) => {
        if (e.response.data.error.code === INSUFFICIENT_RIGHTS_ERROR_CODE) {
          setPasswordChangeMessage({ type: 'fail', message: 'Na túto akciu nemáte oprávnenie.' });
        } else if (e.response.data.error.code === PASSWORD_REUSE_ERROR_CODE) {
          setPasswordChangeMessage({ type: 'fail', message: 'Nové heslo sa musí líšiť od aktuálneho hesla.' });
        } else {
          panic(e);
        }
      });
  }

  return (
    <DashboardLayout
      title={`Úprava užívateľa: ${data?.fullName ?? ''}`}
      breadcrumbs={[
        { title: 'Používatelia', link: USERS_PAGE_PATH.original },
        { title: data?.firstName ?? 'Načítavanie ...' },
      ]}
    >
      {!loading && (
        <UserForm
          onSubmit={onSubmit}
          initialValues={mapUserResponseToFormInitialValues(data, getPermissionTemplate)}
          hideSections={{
            password: true,
            inOrganizations: false,
            inDepartments: false,
            tableProfiles: true,
          }}
          readOnly={{
            organizationId: true,
          }}
          passwordChangeMessage={passwordChangeMessage}
          passwordValidUntil={data.passwordValidUntil}
          onPasswordChange={changePassword}
        />
      )}
    </DashboardLayout>
  );
}
